import Image from 'components/Basic/Image/Image';
import ArticleResults from 'components/Layout/Header/HeaderBottom/Search/Autocomplete/Result/ArticleResults';
import CategoryResults from 'components/Layout/Header/HeaderBottom/Search/Autocomplete/Result/CategoryResults';
import ProductResults from 'components/Layout/Header/HeaderBottom/Search/Autocomplete/Result/ProductResult/ProductResults';
import useTranslation from 'next-translate/useTranslation';
import { twJoin } from 'tailwind-merge';
import { AutocompleteSearchType } from 'types/search';

type AutocompleteProps = {
    autocompleteSearchResults: AutocompleteSearchType | undefined;
    isAutocompleteActive: boolean;
    isPanelDockedFromLeft?: boolean;
    autocompleteSearchQueryValue: string;
    fetching?: boolean;
};

const Autocomplete: FC<AutocompleteProps> = ({
    autocompleteSearchResults,
    isAutocompleteActive,
    isPanelDockedFromLeft,
    autocompleteSearchQueryValue,
}) => {
    const testIdentifier = 'layout-header-search-autocomplete';
    const { t } = useTranslation();

    return (
        <div
            data-testid={testIdentifier}
            className={twJoin(
                'lg:pointer-events-none lg:relative lg:z-aboveMenu lg:w-full lg:origin-[center_top] lg:scale-y-90 lg:opacity-0 lg:transition-all',
                'vl:min-w-[320px] vl:max-w-[576px]',
                'xl:max-w-[800px]',
                'xxl:max-w-[960px]',
                isAutocompleteActive && 'lg:pointer-events-auto lg:scale-y-100 lg:opacity-100',
            )}
        >
            <div
                className={twJoin(
                    'absolute left-0 right-0 z-aboveMenu overflow-hidden bg-white px-6',
                    'vl:right-auto vl:top-1.5 vl:rounded-md vl:px-5',
                    'xl:flex xl:px-0',
                    'notVl:pointer-events-none notVl:origin-[center_top] notVl:scale-y-90 notVl:opacity-0 notVl:transition-all',
                    isPanelDockedFromLeft && 'xl:-translate-x-[320px]',
                    isAutocompleteActive && 'notVl:pointer-events-auto notVl:scale-y-100 notVl:opacity-100',
                )}
            >
                {(() => {
                    if (autocompleteSearchResults === undefined) {
                        return null;
                    }

                    const productsCount = autocompleteSearchResults.productsSearch.totalCount;
                    const categoriesCount = autocompleteSearchResults.categoriesSearch.totalCount;
                    const articlesCount = autocompleteSearchResults.articlesSearch.length;

                    if (productsCount === 0 && categoriesCount === 0 && articlesCount === 0) {
                        return (
                            <div className="flex min-w-[240px] items-center py-5 vl:px-6">
                                <Image alt={t('warning')} height={24} src="/icons/warning.png" width={24} />
                                <span className="flex-1 pl-4 text-small">
                                    {t('Could not find any results for the given query.')}
                                </span>
                            </div>
                        );
                    }

                    return (
                        <>
                            {productsCount > 0 && (
                                <ProductResults
                                    autocompleteSearchQueryValue={autocompleteSearchQueryValue}
                                    products={autocompleteSearchResults.productsSearch.products}
                                />
                            )}
                            {(categoriesCount > 0 || articlesCount > 0) && (
                                <div className="xl:not-first:border-t-none py-5 not-first:border-t not-first:border-grayMidDarker vl:order-1 vl:pt-4 xl:w-[320px] xl:pl-5 xl:pr-9 xl:not-first:bg-grayLighter">
                                    {categoriesCount > 0 && (
                                        <CategoryResults
                                            autocompleteSearchQueryValue={autocompleteSearchQueryValue}
                                            categories={autocompleteSearchResults.categoriesSearch.categories}
                                        />
                                    )}
                                    {articlesCount > 0 && (
                                        <ArticleResults
                                            autocompleteSearchQueryValue={autocompleteSearchQueryValue}
                                            articles={[
                                                ...autocompleteSearchResults.articlesSearch.filter((i) => i.image),
                                                ...autocompleteSearchResults.articlesSearch.filter((i) => !i.image),
                                            ]}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>
        </div>
    );
};

export default Autocomplete;
